import { InboxOutlined } from "@ant-design/icons";
import { Box, styled } from "@mui/material";
import {
  Col,
  Form,
  message,
  Modal,
  Radio,
  Row,
  Upload,
} from "antd";
import { useTranslation } from "react-i18next";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import "./addModal.scss";
import { useEffect, useRef, useState } from "react";
import { FormInstance } from "antd/lib";
import axiosInstance, { axiosUploadInstance } from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";

interface LanguageData {
  id: number;
  code: string;
  name: string;
  flag: string;
  active: boolean;
  as_default: boolean;
  created_date: string;
  last_modified_date: string;
}

interface AddModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  reloadDataEvent: () => void;
  languageId: number;
}

const AddModal: React.FC<AddModalProps> = ({
  open,
  setOpen,
  reloadDataEvent: reloadEvent,
  languageId,
}) => {
  const { t } = useTranslation();
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  const { Dragger } = Upload;
  const formRef = useRef<FormInstance>(null);
  const [flagImage, setFlagImage] = useState<File | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [form] = Form.useForm();
  const [currentLang, setCurrentLang] = useState<LanguageData | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);
  // Bumps per fetch so a late response from a previous language is ignored.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    // Set mounted flag
    isMounted.current = true;
    
    return () => {
      // Clear mounted flag on unmount
      isMounted.current = false;
    };
  }, []);

  const postLanguage = async (data: FormData) => {
    try {
      setLoadingCreate(true);

      await axiosUploadInstance.post(pathAPI.settingLanguage, data);
      setOpen(false);
      messageApi.open({
        type: "success",
        content: t("language.form.created_ok"),
      });
      reloadEvent();
      form.resetFields();
      setFlagImage(null);
      setFileList([]);
    } catch (error) {
      console.error("POST error:", error);
      messageApi.open({
        type: "error",
        content: t("language.form.create_error"),
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  const updateLanguage = async (id: number, data: FormData) => {
    try {
      setLoadingCreate(true);

      await axiosUploadInstance.put(`${pathAPI.settingLanguage}/${id}`, data);
      setOpen(false);
      messageApi.open({
        type: "success",
        content: t("language.form.updated_ok"),
      });
      reloadEvent();
      form.resetFields();
      setFlagImage(null);
      setFileList([]);
    } catch (error) {
      console.error("PUT error:", error);
      messageApi.open({
        type: "error",
        content: t("language.form.update_error"),
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  const onFinish = async () => {
    try {
      // Validate form first
      await form.validateFields();
      
      // Create form data
      const formData = new FormData();
      
      // Create model object
      const modelObj = {
        name: form.getFieldValue("name"),
        code: form.getFieldValue("code"),
        active: form.getFieldValue("active") === true,
        as_default: form.getFieldValue("as_default") === true
      };
      
      // Add file if selected - ensure it's a valid file object
      if (flagImage && flagImage instanceof File) {
        console.log("Adding flagFile to FormData:", {
          name: flagImage.name,
          type: flagImage.type,
          size: flagImage.size
        });
        formData.append('flagFile', flagImage, flagImage.name || 'flag.png');
      } else {
        console.warn("No valid flagImage to upload:", flagImage);
      }
      
      // Add data as application/json blob
      formData.append('data', new Blob([JSON.stringify(modelObj)], {
        type: 'application/json'
      }));
      
      console.log("Sending with exact format:", modelObj);
      
      // Debug FormData contents
      try {
        const entries = Array.from(formData.entries());
        console.log("FormData contents:", entries.map(entry => entry[0]));
        for (const entry of entries) {
          if (entry[0] === 'flagFile') {
            console.log("flagFile entry:", entry[1] instanceof File ? 
              `File: ${(entry[1] as File).name}, ${(entry[1] as File).size} bytes` : 
              "Not a File object");
          }
        }
      } catch (e) {
        console.error("Error logging FormData:", e);
      }
      
      if (languageId !== 0) {
        // When editing, file is optional
        updateLanguage(languageId, formData);
      } else {
        // For new languages, flag is required
        if (!flagImage) {
          messageApi.open({
            type: "error",
            content: t("language.form.flag_required_msg"),
          });
          return;
        }
        postLanguage(formData);
      }
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  const handleCancel = () => {
    requestSeqRef.current += 1;
    setLoadingDetails(false);
    setOpen(false);
    form.resetFields();
    setCurrentLang(null);
    setFileList([]);
    setFlagImage(null);
  };

  const getLanguageDetail = async () => {
    const seq = ++requestSeqRef.current;

    try {
      setLoadingDetails(true);
      const response = await axiosInstance.get(`admin/setting/languages/${languageId}`);
      if (seq !== requestSeqRef.current) return;

      if (!isMounted.current) return;

      if (response.data?.data) {
        const detailLanguage = response.data.data;
        setCurrentLang(detailLanguage);
        form.setFieldsValue({
          name: detailLanguage.name,
          code: detailLanguage.code,
          active: detailLanguage.active,
          as_default: detailLanguage.as_default
        });
      }
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      if (isMounted.current) {
        console.error("Error fetching language details:", error);
        message.error(t("language.detail_error"));
      }
    } finally {
      if (seq === requestSeqRef.current && isMounted.current) {
        setLoadingDetails(false);
      }
    }
  };

  useEffect(() => {
    if (open) {
      if (languageId !== 0) {
        // Reset form before fetching new data
        form.resetFields();
        // Immediately set default values for a better UX while loading
        form.setFieldsValue({ 
          active: true,
          as_default: false
        });
        setCurrentLang(null);
        setFileList([]);
        getLanguageDetail();
      } else {
        // Reset form for new language
        form.resetFields();
        setCurrentLang(null);
        setFileList([]);
        // Default values for new language
        form.setFieldsValue({ 
          active: true,
          as_default: false
        });
      }
    }
  }, [languageId, open, form]);

  return (
    <Modal
      className="add_admin"
      centered
      open={open}
      onOk={() => setOpen(false)}
      onCancel={handleCancel}
      okText={t("btn.save")}
      cancelText={t("btn.cancel")}
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={handleCancel}>{t("btn.cancel")}</CancelButton>
          <ActionButton onClick={onFinish} loading={loadingCreate} disabled={loadingDetails}>
            {t("btn.ok")}
          </ActionButton>
        </Box>
      }
    >
      <div className="header_style">
        {languageId === 0 ? t("language.form.add_title") : t("language.form.edit_title")}
        {loadingDetails && <span className="ml-2 inline-block"><span className="anticon-spin">
          <svg viewBox="0 0 1024 1024" focusable="false" data-icon="loading" width="1em" height="1em" fill="currentColor" aria-hidden="true">
            <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
          </svg>
        </span></span>}
      </div>

      <div className="relative">
        {loadingDetails && (
          <div className="absolute inset-0 bg-[var(--app-surface)] bg-opacity-80 flex justify-center items-center z-10">
            <span className="anticon-spin" style={{ fontSize: '24px' }}>
              <svg viewBox="0 0 1024 1024" focusable="false" data-icon="loading" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
              </svg>
            </span>
          </div>
        )}
        
        <StyledForm {...layout} onFinish={onFinish} ref={formRef} form={form}>
          <Row gutter={[6, 0]}>
            <Col span={24}>
              <Form.Item
                name="name"
                label={t("language.form.name")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: t("language.form.name_required"),
                  },
                ]}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="code"
                label={t("language.form.code")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: t("language.form.code_required"),
                  },
                ]}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="flag"
                label={t("language.form.flag")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: languageId === 0,
                    message: t("language.form.flag_required"),
                  },
                ]}
              >
                <Dragger
                  fileList={fileList}
                  multiple={false}
                  beforeUpload={(file) => {
                    console.log("beforeUpload file:", file);
                    return false; // Prevent auto-upload
                  }}
                  onChange={(info) => {
                    console.log("Upload onChange info:", info);
                    // Update the fileList for the UI
                    setFileList(info.fileList.slice(-1));
                    
                    // Store the actual File object for submission
                    if (info.file.originFileObj) {
                      console.log("Setting flagImage from originFileObj:", {
                        name: info.file.originFileObj.name,
                        type: info.file.originFileObj.type,
                        size: info.file.originFileObj.size
                      });
                      setFlagImage(info.file.originFileObj);
                    } else if (info.file instanceof File) {
                      console.log("Setting flagImage from File:", {
                        name: info.file.name,
                        type: info.file.type,
                        size: info.file.size
                      });
                      setFlagImage(info.file);
                    } else {
                      console.warn("No valid file object found in upload event:", info.file);
                    }
                  }}
                  onRemove={() => {
                    console.log("Removing flagImage");
                    setFileList([]);
                    setFlagImage(null);
                    return true;
                  }}
                  accept="image/*"
                  listType="picture"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {t("language.form.drag_hint")}
                  </p>
                  <p className="ant-upload-hint">
                    {t("language.form.drag_sub")}
                  </p>
                </Dragger>
              </Form.Item>
            </Col>
            {currentLang && currentLang.flag ? (
              <Col span={24}>
                <div className="text-center mb-3">
                  <p>{t("language.form.current_flag")}</p>
                  <img src={currentLang.flag} alt="flag" className="w-14 h-14 rounded-md mx-auto" />
                </div>
              </Col>
            ) : null}
            <Col span={24}>
              <Form.Item
                name="active"
                label={t("language.form.status")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={true}>{t("language.form.active")}</Radio>
                  <Radio value={false}>{t("language.form.inactive")}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="as_default"
                label={t("language.form.as_default")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                initialValue={false}
              >
                <Radio.Group>
                  <Radio value={true}>{t("language.form.yes")}</Radio>
                  <Radio value={false}>{t("language.form.no")}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </StyledForm>
      </div>
      {contextHolder}
    </Modal>
  );
};

export default AddModal;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 10px;
  }
`;
